import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  Cable,
  Usb,
  ExternalLink,
  Plug,
  Unplug,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  ensurePilotWiringSeed,
  getProject,
  updateProject,
  type EngineeringProject,
} from "@/lib/prometheusProjectsStore";
import {
  getDeviceByProject,
  kindFromMcuLabel,
  setDeviceStatus,
  upsertDevice,
  type DeviceRecord,
} from "@/lib/deviceRegistryStore";
import type { ProjectWiring, WiringPin, WiringStep } from "@/lib/projectWiringTypes";
import { WiringIsometricSvg } from "@/components/prometheus/os/WiringIsometricSvg";
import { requestHardwareApproval } from "@/lib/prometheusApprovals";
import { safetyProgress } from "@/lib/prometheusProjectMeta";

export type ProjectWiringPanelProps = {
  projectId: string;
  className?: string;
};

function normalizePin(raw: unknown): WiringPin | null {
  if (raw == null || typeof raw !== "object") return null;
  const p = raw as Record<string, unknown>;
  if (typeof p.id !== "string" || !p.id) return null;
  if (typeof p.pin !== "string" || typeof p.signal !== "string" || typeof p.to !== "string") {
    return null;
  }
  const mcu =
    p.mcu === "ESP32" || p.mcu === "Arduino" || p.mcu === "Other" ? p.mcu : "Other";
  return {
    id: p.id,
    mcu,
    pin: p.pin,
    signal: p.signal,
    to: p.to,
    notes: typeof p.notes === "string" ? p.notes : undefined,
  };
}

function normalizeStep(raw: unknown): WiringStep | null {
  if (raw == null || typeof raw !== "object") return null;
  const s = raw as Record<string, unknown>;
  if (typeof s.id !== "string" || !s.id) return null;
  if (typeof s.title !== "string" || typeof s.detail !== "string") return null;
  return {
    id: s.id,
    title: s.title,
    detail: s.detail,
    done: typeof s.done === "boolean" ? s.done : undefined,
  };
}

/** Normalize LS / partial wiring so empty or broken schema never crashes the panel. */
function normalizeWiring(raw: ProjectWiring | undefined | null): ProjectWiring | null {
  if (raw == null || typeof raw !== "object") return null;
  return {
    mcuLabel: typeof raw.mcuLabel === "string" ? raw.mcuLabel : "",
    overview: typeof raw.overview === "string" ? raw.overview : "",
    pins: Array.isArray(raw.pins)
      ? raw.pins.map(normalizePin).filter((p): p is WiringPin => p != null)
      : [],
    steps: Array.isArray(raw.steps)
      ? raw.steps.map(normalizeStep).filter((s): s is WiringStep => s != null)
      : [],
    usbHint: typeof raw.usbHint === "string" ? raw.usbHint : undefined,
  };
}

export function ProjectWiringPanel({ projectId, className }: ProjectWiringPanelProps) {
  const navigate = useNavigate();
  const [project, setProject] = useState<EngineeringProject | null>(null);
  const [device, setDevice] = useState<DeviceRecord | null>(null);
  const [rev, setRev] = useState(0);
  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);

  const reload = useCallback(() => {
    ensurePilotWiringSeed();
    setProject(getProject(projectId));
    setDevice(getDeviceByProject(projectId));
    setRev((n) => n + 1);
  }, [projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const wiring = normalizeWiring(project?.wiring);
  const isOnline = device?.status === "online";

  const toggleStep = (stepId: string) => {
    const latest = getProject(projectId);
    const current = normalizeWiring(latest?.wiring);
    if (!latest || !current) return;
    const steps: WiringStep[] = current.steps.map((s) =>
      s.id === stepId ? { ...s, done: !s.done } : s
    );
    const nextWiring: ProjectWiring = { ...current, steps };
    const updated = updateProject(projectId, { wiring: nextWiring });
    if (updated) {
      setProject(updated);
      setRev((n) => n + 1);
    } else {
      reload();
    }
  };

  const safetyWarn = () => {
    const prog = safetyProgress(projectId, project?.title, project?.discipline);
    if (prog.allCriticalOk) return "";
    const miss = prog.criticalMissing.map((i) => i.label).join(", ");
    return `\n\n⚠ Seguridad: faltan críticos (${miss}). Completa la pestaña Seguridad del Artifact si vas a energizar.`;
  };

  const onProbarUsb = async () => {
    const ok = await requestHardwareApproval({
      title: "Probar USB / Web Serial",
      detail:
        "Se abrirá el hub MCP para elegir puerto serie. Puede solicitar acceso al dispositivo USB del navegador." +
        safetyWarn(),
    });
    if (!ok) {
      toast.message("USB cancelado");
      return;
    }
    toast.info("Abriendo hub Web Serial / MCP…");
    navigate("/herramientas/mcp");
  };

  const onMarkConnected = async () => {
    const mcu = wiring?.mcuLabel ?? "MCU";
    const alias = project?.title ? `${mcu} · ${project.title}` : mcu;
    const ok = await requestHardwareApproval({
      title: "Marcar en Monitor (en vivo)",
      detail:
        `«${alias}» aparecerá como conectado en Monitor. Solo confirma si la placa ya responde / está cableada.` +
        safetyWarn(),
    });
    if (!ok) {
      toast.message("Monitor cancelado");
      return;
    }
    const updated = upsertDevice({
      projectId,
      alias,
      kind: kindFromMcuLabel(mcu),
      status: "online",
      lastSeenAt: new Date().toISOString(),
      transport: "usb",
    });
    setDevice(updated);
    toast.success("Dispositivo en Monitor (en vivo)");
  };

  const onDisconnectMonitor = async () => {
    const current = getDeviceByProject(projectId);
    if (!current) {
      setDevice(null);
      toast.info("No había dispositivo en Monitor");
      return;
    }
    const ok = await requestHardwareApproval({
      title: "Desconectar del Monitor",
      detail: `Quitar «${current.alias || current.id}» de la lista en vivo (no cierra el puerto USB del SO).`,
    });
    if (!ok) {
      toast.message("Desconexión cancelada");
      return;
    }
    const updated = setDeviceStatus(current.id, "offline");
    setDevice(updated);
    toast.message("Desconectado del Monitor");
  };

  if (!project) {
    return (
      <p className={cn("text-[13px] text-muted-foreground", className)}>
        No se encontró el proyecto.
      </p>
    );
  }

  if (!wiring) {
    return (
      <div
        className={cn(
          "rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center",
          className
        )}
      >
        <Cable className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
        <p className="text-[12px] text-muted-foreground">
          Este proyecto aún no define conexiones
        </p>
      </div>
    );
  }

  return (
    <div key={rev} className={cn("space-y-6 font-mono-tech", className)}>
      {/* Cómo se ve */}
      <section>
        <h3 className="text-[10px] uppercase tracking-[0.14em] text-primary font-bold mb-2">
          Cómo se ve
        </h3>
        <p className="text-[12px] text-muted-foreground leading-relaxed mb-3">
          <span className="text-foreground font-medium">{wiring.mcuLabel}</span>
          {" — "}
          {wiring.overview}
        </p>
        {wiring.pins.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">
            Sin pines — no hay diagrama de cableado.
          </p>
        ) : (
          <div className="rounded-lg border border-sky-500/25 bg-[#071018] px-2 py-3">
            <WiringIsometricSvg
              pins={wiring.pins}
              mcuLabel={wiring.mcuLabel}
              selectedPinId={selectedPinId}
              onSelectPin={(id) =>
                setSelectedPinId((prev) => (prev === id ? null : id))
              }
            />
          </div>
        )}
      </section>

      {/* Conexiones */}
      <section>
        <h3 className="text-[10px] uppercase tracking-[0.14em] text-primary font-bold mb-2">
          Conexiones
        </h3>
        {wiring.pins.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">Sin pines definidos.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border/60">
            <table className="w-full text-[11px] text-left">
              <thead>
                <tr className="border-b border-border/60 bg-muted/30 text-[9px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-2 py-1.5 font-bold">MCU</th>
                  <th className="px-2 py-1.5 font-bold">Pin</th>
                  <th className="px-2 py-1.5 font-bold">Señal</th>
                  <th className="px-2 py-1.5 font-bold">Hacia</th>
                  <th className="px-2 py-1.5 font-bold">Notas</th>
                </tr>
              </thead>
              <tbody>
                {wiring.pins.map((pin) => {
                  const selected = selectedPinId === pin.id;
                  return (
                    <tr
                      key={pin.id}
                      onClick={() =>
                        setSelectedPinId((prev) => (prev === pin.id ? null : pin.id))
                      }
                      className={cn(
                        "border-b border-border/40 last:border-0 cursor-pointer transition-colors",
                        selected ? "bg-primary/10" : "hover:bg-muted/25"
                      )}
                    >
                      <td className="px-2 py-1.5 text-foreground/80">{pin.mcu}</td>
                      <td className="px-2 py-1.5 font-medium text-foreground">{pin.pin}</td>
                      <td className="px-2 py-1.5 text-primary/90">{pin.signal}</td>
                      <td className="px-2 py-1.5 text-foreground/90">{pin.to}</td>
                      <td className="px-2 py-1.5 text-muted-foreground">{pin.notes ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Cómo cablear */}
      <section>
        <h3 className="text-[10px] uppercase tracking-[0.14em] text-primary font-bold mb-2">
          Cómo cablear
        </h3>
        {wiring.steps.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">Sin pasos de cableado.</p>
        ) : (
          <ul className="space-y-2">
            {wiring.steps.map((step) => {
              const done = Boolean(step.done);
              return (
                <li key={step.id}>
                  <button
                    type="button"
                    onClick={() => toggleStep(step.id)}
                    className={cn(
                      "w-full flex items-start gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors cursor-pointer",
                      done
                        ? "border-primary/30 bg-primary/5"
                        : "border-border/60 bg-card/20 hover:bg-muted/30"
                    )}
                  >
                    {done ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                    ) : (
                      <Circle className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                    )}
                    <span className="min-w-0">
                      <span
                        className={cn(
                          "block text-[12px] font-medium",
                          done ? "text-muted-foreground line-through" : "text-foreground"
                        )}
                      >
                        {step.title}
                      </span>
                      <span className="block text-[11px] text-muted-foreground leading-relaxed mt-0.5">
                        {step.detail}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onMarkConnected}
            disabled={isOnline}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[12px] font-bold transition-colors cursor-pointer",
              isOnline
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600/80 cursor-default"
                : "bg-primary/15 border-primary/40 text-primary hover:bg-primary/25"
            )}
          >
            <Plug className="w-3.5 h-3.5" />
            {isOnline ? "Conectado en Monitor" : "Marcar como conectado y en Monitor"}
          </button>
          <button
            type="button"
            onClick={onDisconnectMonitor}
            disabled={!device || !isOnline}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[12px] font-medium transition-colors",
              device && isOnline
                ? "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/30 cursor-pointer"
                : "border-border/40 text-muted-foreground/50 cursor-not-allowed"
            )}
          >
            <Unplug className="w-3.5 h-3.5" />
            Desconectar / quitar del Monitor
          </button>
        </div>
        <p className="mt-2 text-[10px] text-muted-foreground leading-relaxed">
          El plano de cableado sigue visible aunque el dispositivo esté offline. Solo el
          Monitor lista equipos marcados en vivo.
        </p>
      </section>

      {/* USB */}
      <section>
        <h3 className="text-[10px] uppercase tracking-[0.14em] text-primary font-bold mb-2">
          USB
        </h3>
        {wiring.usbHint ? (
          <p className="text-[12px] text-muted-foreground mb-3">{wiring.usbHint}</p>
        ) : (
          <p className="text-[12px] text-muted-foreground mb-3">
            Usa el hub Web Serial para probar la placa por USB.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onProbarUsb}
            className="inline-flex items-center gap-2 rounded-lg bg-primary/15 border border-primary/40 px-3 py-2 text-[12px] font-bold text-primary hover:bg-primary/25 transition-colors cursor-pointer"
          >
            <Usb className="w-3.5 h-3.5" />
            Probar USB
          </button>
          <Link
            to="/herramientas/mcp"
            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary transition-colors"
          >
            Abrir hub MCP
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </section>
    </div>
  );
}
